import { ITheme } from '@/types/theme';

/**
 * Get background classes for a section based on theme and background type
 * Dark mode always uses default colors (no theme)
 */
export function getThemeBackgroundClass(
  theme: ITheme,
  backgroundType: 'primary' | 'secondary' | null
): string {
  // Primary background: white in light mode, dark gray in dark mode
  if (backgroundType === 'primary') {
    return 'bg-white dark:bg-gray-900';
  }

  // Secondary background: themed gradient in light mode, solid dark in dark mode
  if (!theme) {
    // Default secondary (blue-ish)
    return 'bg-gradient-to-b from-blue-50/30 to-blue-50/50 dark:!bg-none dark:bg-gray-800';
  }

  const themeGradients: Record<NonNullable<ITheme>, string> = {
    blue: 'bg-gradient-to-b from-blue-50/30 to-blue-50/50 dark:!bg-none dark:bg-gray-800',
    purple:
      'bg-gradient-to-b from-purple-50/30 to-purple-50/50 dark:!bg-none dark:bg-gray-800',
    green:
      'bg-gradient-to-b from-green-50/30 to-green-50/50 dark:!bg-none dark:bg-gray-800',
    orange:
      'bg-gradient-to-b from-orange-50/30 to-orange-50/50 dark:!bg-none dark:bg-gray-800',
    pink: 'bg-gradient-to-b from-pink-100/40 to-pink-100/60 dark:!bg-none dark:bg-gray-800',
    red: 'bg-gradient-to-b from-red-50/30 to-red-50/50 dark:!bg-none dark:bg-gray-800',
    teal: 'bg-gradient-to-b from-teal-50/30 to-teal-50/50 dark:!bg-none dark:bg-gray-800',
    cyan: 'bg-gradient-to-b from-cyan-50/30 to-cyan-50/50 dark:!bg-none dark:bg-gray-800',
    indigo:
      'bg-gradient-to-b from-indigo-50/30 to-indigo-50/50 dark:!bg-none dark:bg-gray-800',
    rose: 'bg-gradient-to-b from-rose-50/30 to-rose-50/50 dark:!bg-none dark:bg-gray-800',
    amber:
      'bg-gradient-to-b from-amber-50/30 to-amber-50/50 dark:!bg-none dark:bg-gray-800',
    emerald:
      'bg-gradient-to-b from-emerald-50/30 to-emerald-50/50 dark:!bg-none dark:bg-gray-800',
  };

  return themeGradients[theme];
}

/**
 * Get text color for theme highlights
 */
export function getThemeTextClass(theme: ITheme): string {
  if (!theme) {
    return 'text-blue-600 dark:text-blue-400';
  }

  const themeTextColors: Record<NonNullable<ITheme>, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    green: 'text-green-700 dark:text-green-400',
    orange: 'text-orange-700 dark:text-orange-400',
    pink: 'text-pink-600 dark:text-pink-400',
    red: 'text-red-600 dark:text-red-400',
    teal: 'text-teal-700 dark:text-teal-400',
    cyan: 'text-cyan-700 dark:text-cyan-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    rose: 'text-rose-600 dark:text-rose-400',
    amber: 'text-amber-700 dark:text-amber-400',
    emerald: 'text-emerald-700 dark:text-emerald-400',
  };

  return themeTextColors[theme];
}

/**
 * Get strong text color for theme in light mode only
 * Returns high-contrast colors for better readability
 */
export function getThemeTextColor(theme: ITheme): string {
  if (!theme) {
    return 'text-blue-700';
  }

  const themeStrongTextColors: Record<NonNullable<ITheme>, string> = {
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    green: 'text-green-700',
    orange: 'text-orange-700',
    pink: 'text-pink-700',
    red: 'text-red-700',
    teal: 'text-teal-700',
    cyan: 'text-cyan-700',
    indigo: 'text-indigo-700',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
    emerald: 'text-emerald-700',
  };

  return themeStrongTextColors[theme];
}

/**
 * Get stroke color for theme
 */
export function getThemeStrokeClass(theme: ITheme): string {
  if (!theme) {
    return 'stroke-[#3880ff]/30 dark:stroke-blue-800';
  }

  const themeStrokeColors: Record<NonNullable<ITheme>, string> = {
    blue: 'stroke-blue-300 dark:stroke-blue-800',
    purple: 'stroke-purple-300 dark:stroke-purple-800',
    green: 'stroke-green-300 dark:stroke-green-800',
    orange: 'stroke-orange-300 dark:stroke-orange-800',
    pink: 'stroke-pink-300 dark:stroke-pink-800',
    red: 'stroke-red-300 dark:stroke-red-800',
    teal: 'stroke-teal-300 dark:stroke-teal-800',
    cyan: 'stroke-cyan-300 dark:stroke-cyan-800',
    indigo: 'stroke-indigo-300 dark:stroke-indigo-800',
    rose: 'stroke-rose-300 dark:stroke-rose-800',
    amber: 'stroke-amber-300 dark:stroke-amber-800',
    emerald: 'stroke-emerald-300 dark:stroke-emerald-800',
  };

  return themeStrokeColors[theme];
}

/**
 * Get the actual hex/rgb color value for a theme
 * Returns color suitable for inline styles
 */
export function getThemeColorValue(theme: ITheme): string {
  if (!theme) {
    return '#3880ff'; // default blue
  }

  const themeColors: Record<NonNullable<ITheme>, string> = {
    blue: '#3b82f6',
    purple: '#a855f7',
    green: '#22c55e',
    orange: '#f97316',
    pink: '#ec4899',
    red: '#ef4444',
    teal: '#14b8a6',
    cyan: '#06b6d4',
    indigo: '#6366f1',
    rose: '#f43f5e',
    amber: '#f59e0b',
    emerald: '#10b981',
  };

  return themeColors[theme];
}

/**
 * Get border color for theme
 */
export function getThemeBorderClass(theme: ITheme): string {
  if (!theme) {
    return 'border-blue-600 dark:border-blue-400';
  }

  const themeBorderColors: Record<NonNullable<ITheme>, string> = {
    blue: 'border-blue-600 dark:border-blue-400',
    purple: 'border-purple-600 dark:border-purple-400',
    green: 'border-green-600 dark:border-green-400',
    orange: 'border-orange-600 dark:border-orange-400',
    pink: 'border-pink-600 dark:border-pink-400',
    red: 'border-red-600 dark:border-red-400',
    teal: 'border-teal-600 dark:border-teal-400',
    cyan: 'border-cyan-600 dark:border-cyan-400',
    indigo: 'border-indigo-600 dark:border-indigo-400',
    rose: 'border-rose-600 dark:border-rose-400',
    amber: 'border-amber-600 dark:border-amber-400',
    emerald: 'border-emerald-600 dark:border-emerald-400',
  };

  return themeBorderColors[theme];
}

/**
 * Get button classes based on theme
 */
export function getThemeButtonClass(theme: ITheme): string {
  if (!theme) {
    return 'bg-blue-700 hover:bg-blue-800';
  }

  const themeButtonClasses: Record<NonNullable<ITheme>, string> = {
    blue: 'bg-blue-700 hover:bg-blue-800 dark:bg-blue-700 dark:hover:bg-blue-800',
    purple:
      'bg-purple-700 hover:bg-purple-800 dark:bg-purple-700 dark:hover:bg-purple-800',
    green:
      'bg-green-700 hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-800',
    orange:
      'bg-orange-700 hover:bg-orange-800 dark:bg-orange-700 dark:hover:bg-orange-800',
    pink: 'bg-pink-700 hover:bg-pink-800 dark:bg-pink-700 dark:hover:bg-pink-800',
    red: 'bg-red-700 hover:bg-red-800 dark:bg-red-700 dark:hover:bg-red-800',
    teal: 'bg-teal-700 hover:bg-teal-800 dark:bg-teal-700 dark:hover:bg-teal-800',
    cyan: 'bg-cyan-700 hover:bg-cyan-800 dark:bg-cyan-700 dark:hover:bg-cyan-800',
    indigo:
      'bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-700 dark:hover:bg-indigo-800',
    rose: 'bg-rose-700 hover:bg-rose-800 dark:bg-rose-700 dark:hover:bg-rose-800',
    amber:
      'bg-amber-700 hover:bg-amber-800 dark:bg-amber-700 dark:hover:bg-amber-800',
    emerald:
      'bg-emerald-700 hover:bg-emerald-800 dark:bg-emerald-700 dark:hover:bg-emerald-800',
  };

  return themeButtonClasses[theme];
}

/**
 * Get badge background color for theme
 * Light backgrounds suitable for category tags
 */
export function getThemeBadgeBackgroundClass(theme: ITheme): string {
  if (!theme) {
    return 'bg-blue-100 dark:bg-blue-900/30';
  }

  const themeBadgeBackgrounds: Record<NonNullable<ITheme>, string> = {
    blue: 'bg-blue-100 dark:bg-blue-900/30',
    purple: 'bg-purple-100 dark:bg-purple-900/30',
    green: 'bg-green-100 dark:bg-green-900/30',
    orange: 'bg-orange-100 dark:bg-orange-900/30',
    pink: 'bg-pink-100 dark:bg-pink-900/30',
    red: 'bg-red-100 dark:bg-red-900/30',
    teal: 'bg-teal-100 dark:bg-teal-900/30',
    cyan: 'bg-cyan-100 dark:bg-cyan-900/30',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30',
    rose: 'bg-rose-100 dark:bg-rose-900/30',
    amber: 'bg-amber-100 dark:bg-amber-900/30',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/30',
  };

  return themeBadgeBackgrounds[theme];
}

/**
 * Get badge text color for theme
 * Dark text colors suitable for category tags
 */
export function getThemeBadgeTextClass(theme: ITheme): string {
  if (!theme) {
    return 'text-blue-800 dark:text-blue-200';
  }

  const themeBadgeTextColors: Record<NonNullable<ITheme>, string> = {
    blue: 'text-blue-800 dark:text-blue-200',
    purple: 'text-purple-800 dark:text-purple-200',
    green: 'text-green-800 dark:text-green-200',
    orange: 'text-orange-800 dark:text-orange-200',
    pink: 'text-pink-800 dark:text-pink-200',
    red: 'text-red-800 dark:text-red-200',
    teal: 'text-teal-800 dark:text-teal-200',
    cyan: 'text-cyan-800 dark:text-cyan-200',
    indigo: 'text-indigo-800 dark:text-indigo-200',
    rose: 'text-rose-800 dark:text-rose-200',
    amber: 'text-amber-800 dark:text-amber-200',
    emerald: 'text-emerald-800 dark:text-emerald-200',
  };

  return themeBadgeTextColors[theme];
}

/**
 * Get badge border color for theme
 * Light border colors suitable for category badges
 */
export function getThemeBadgeBorderClass(theme: ITheme): string {
  if (!theme) {
    return 'border-blue-100 dark:border-blue-900';
  }

  const themeBadgeBorders: Record<NonNullable<ITheme>, string> = {
    blue: 'border-blue-100 dark:border-blue-900',
    purple: 'border-purple-100 dark:border-purple-900',
    green: 'border-green-100 dark:border-green-900',
    orange: 'border-orange-100 dark:border-orange-900',
    pink: 'border-pink-100 dark:border-pink-900',
    red: 'border-red-100 dark:border-red-900',
    teal: 'border-teal-100 dark:border-teal-900',
    cyan: 'border-cyan-100 dark:border-cyan-900',
    indigo: 'border-indigo-100 dark:border-indigo-900',
    rose: 'border-rose-100 dark:border-rose-900',
    amber: 'border-amber-100 dark:border-amber-900',
    emerald: 'border-emerald-100 dark:border-emerald-900',
  };

  return themeBadgeBorders[theme];
}

/**
 * Get input focus ring color for theme
 * Used for form inputs to highlight the active field
 */
export function getThemeFocusRingClass(theme: ITheme): string {
  if (!theme) {
    return 'focus:ring-blue-500 dark:focus:ring-blue-400';
  }

  const themeFocusRingColors: Record<NonNullable<ITheme>, string> = {
    blue: 'focus:ring-blue-500 dark:focus:ring-blue-400',
    purple: 'focus:ring-purple-500 dark:focus:ring-purple-400',
    green: 'focus:ring-green-500 dark:focus:ring-green-400',
    orange: 'focus:ring-orange-500 dark:focus:ring-orange-400',
    pink: 'focus:ring-pink-500 dark:focus:ring-pink-400',
    red: 'focus:ring-red-500 dark:focus:ring-red-400',
    teal: 'focus:ring-teal-500 dark:focus:ring-teal-400',
    cyan: 'focus:ring-cyan-500 dark:focus:ring-cyan-400',
    indigo: 'focus:ring-indigo-500 dark:focus:ring-indigo-400',
    rose: 'focus:ring-rose-500 dark:focus:ring-rose-400',
    amber: 'focus:ring-amber-500 dark:focus:ring-amber-400',
    emerald: 'focus:ring-emerald-500 dark:focus:ring-emerald-400',
  };

  return themeFocusRingColors[theme];
}

// Map categories to themes
export const getCategoryTheme = (category: string): ITheme => {
  const themeMap: Record<string, ITheme> = {
    'Booking & Scheduling': 'blue',
    'Customers & Forms': 'purple',
    'Payments & Memberships': 'green',
    'Communication & Growth': 'orange',
    'Staff & Operations': 'teal',
    'Website & Integrations': 'indigo',
    預約與排程: 'blue',
    顧客與表單: 'purple',
    付款與會員方案: 'green',
    溝通與成長: 'orange',
    團隊與營運: 'teal',
    網站與整合: 'indigo',
    // English
    Essentials: 'blue',
    Extras: 'purple',
    'Social Media': 'pink',
    Professional: 'red',
    // Chinese
    必備功能: 'blue',
    額外功能: 'purple',
    社群媒體: 'pink',
    專業功能: 'red',
  };
  return themeMap[category] || 'blue';
};
